import React, { useState, useEffect, useRef, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import punjabPeople from "../data/punjabPeople.json";
import "../css/pcmTracker.css";

const API_URL = process.env.REACT_APP_API_URL;

// Fixed PCM roster (names + PC mapped). Daily activity is entered on top.
// Temporary (2YNAS D2D period): PCMs are on door-to-door visits and hold no
// meetings, so MoM counts show "NA" and a notice banner is shown on the page,
// the PDF, and the emailed report. Flip SHOW_MOM_AS_NA to false to restore counts.
const SHOW_MOM_AS_NA = false;
const PCM_NOTICE = "Due to 2YNAS D2D visits by PCM no Meetings are being held";

const DEFAULT_PCM = [
  ["Sashidhar Betha", "Srikakulam"],
  ["Shivaji Perka", "Vizianagaram"],
  ["Joseph Raja", "Araku (Manyam district)"],
  ["Vacant", "Visakhapatnam"],
  ["Anudeep", "Anakapalli"],
  ["Annepu Jagannath", "Rajahmundry"],
  ["Vacant", "Kakinada"],
  ["Vempati Sumanth", "Amalapuram"],
  ["Niteesh Kumar Reddy", "Narsapuram"],
  ["Anvesh Manchoju", "Eluru"],
  ["S Venkatesh", "Bapatla"],
  ["Praveen Kumar Maniyari", "Guntur"],
  ["Vishnu Vardhan Reddy", "Machilipatnam"],
  ["K Teja Kumar", "Narasaraopet"],
  ["Praharaju Viswa Varun", "Vijayawada"],
  ["Venkata Bhanu Prakash", "Ongole"],
  ["Srinivas", "Nellore"],
  ["Shanigarapu Anvesh", "Rajampet"],
  ["Vacant", "Tirupati"],
  ["Vangari Sai Kiran", "Chittoor"],
  ["Sri Nihith Tenneti", "Anantapur"],
  ["Jetti Yashwanth Kumar", "Kadapa"],
  ["Shiva Vardhan", "Hindupur"],
  ["Kishore Banka", "Kurnool"],
  ["Chandan Kumar", "Nandyal"],
].map(([pcmName, pcMapped], i) => ({
  slNo: i + 1,
  pcmName,
  pcMapped,
  attendance: pcmName === "Vacant" ? "Vacant" : "",
  cabUsed: "",
  escalationRaised: "",
  oppositionRaised: "",
  meetingConducted: "",
  note: "",
}));

const DEFAULT_ZONAL = [
  ["Zone 1", "Srikakulam, Vizianagaram, Araku", "Vamshi Vardhan"],
  ["Zone 1", "Visakhapatnam, Anakapalli", "Vivekanand"],
  ["Zone 3", "Bapatla, Machilipatnam, Narsaraopeta", "Bharani Kumar"],
  ["Zone 3", "Guntur, Vijayawada", "Jajula Ramalingeshwar"],
  ["Zone 4", "Zone 4", "Medidi Nikhil Kumar"],
  ["Zone 5", "Zone 5", "Pranav Raj"],
].map(([zone, pcsMapped, zonal], i) => ({
  slNo: i + 1,
  zone,
  pcsMapped,
  zonal,
  meetingsConducted: "",
}));

const ATTENDANCE = ["", "Present", "Absent", "Vacant"];
const CAB = ["", "Yes", "No", "NA"];

// Punjab roster: one row per AC. The "AC Mapped" column holds each field
// person's actual Assembly Constituencies (from the Punjab mapping).
const PUNJAB_PCM = punjabPeople
  .filter((p) => (p.role || "").toLowerCase() === "acm")
  .flatMap((p) =>
    (p.acs && p.acs.length ? p.acs : [{ ac_name: p.region || "" }]).map((ac) => ({
      pcmName: p.name,
      acName: ac.ac_name,
    }))
  )
  .map((row, i) => ({
    slNo: i + 1,
    pcmName: row.pcmName,
    pcMapped: row.acName, // rendered under the "AC Mapped" header for Punjab
    attendance: "",
    cabUsed: "",
    escalationRaised: "",
    oppositionRaised: "",
    meetingConducted: "",
    note: "",
  }));

const PUNJAB_ZONAL = punjabPeople
  .filter((p) => (p.role || "").toLowerCase() === "zonal")
  .map((p, i) => ({
    slNo: i + 1,
    zone: p.region || "", // header shows "Region" for Punjab
    pcsMapped: (p.acs || []).map((a) => a.ac_name).join(", "), // header shows "AC Mapped"
    zonal: p.name,
    meetingsConducted: "",
  }));

const PcmActivityTracker = () => {
  const [pcmRows, setPcmRows] = useState(DEFAULT_PCM);
  const [zonalRows, setZonalRows] = useState(DEFAULT_ZONAL);
  const [note, setNote] = useState("");
  const [meetingsScheduled, setMeetingsScheduled] = useState("");
  const [zonalsPresent, setZonalsPresent] = useState(DEFAULT_ZONAL.length);
  const [zonalsAbsent, setZonalsAbsent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [momCounts, setMomCounts] = useState([]); // [{ name, count }]
  const [pcmLocation, setPcmLocation] = useState(null); // null = not loaded yet
  const isPunjab = pcmLocation === "Punjab";
  const stateQuery = isPunjab ? "?state=Punjab" : "";

  const printRef = useRef();
  const today = new Date();
  const dateLabel = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Normalise a name for fuzzy comparison: lowercase, strip punctuation,
  // sort the word tokens so "Betha Sashidhar" == "Sashidhar Betha".
  const normName = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean)
      .sort()
      .join(" ");

  // Look up a PCM's meeting count by fuzzy-matching their name.
  const momCountFor = (pcmName) => {
    if (!pcmName || pcmName === "Vacant") return 0;
    const target = normName(pcmName);
    if (!target) return 0;
    // exact normalised match first
    let hit = momCounts.find((c) => normName(c.name) === target);
    if (hit) return hit.count;

    // token-level fuzzy: every token of the shorter name has a close match
    // in the longer name (handles minor misspellings, e.g. Jagganath/Jagannath)
    const close = (a, b) => {
      if (a === b) return true;
      if (a.length < 3 || b.length < 3) return false;
      if (a.startsWith(b) || b.startsWith(a)) return true;
      // allow ~1 char difference for similar-length tokens
      if (Math.abs(a.length - b.length) > 1) return false;
      let diff = 0;
      const len = Math.max(a.length, b.length);
      for (let i = 0; i < len; i++) if (a[i] !== b[i]) diff++;
      return diff <= 2;
    };
    const tt = target.split(" ");
    hit = momCounts.find((c) => {
      const ct = normName(c.name).split(" ");
      const short = tt.length <= ct.length ? tt : ct;
      const long = tt.length <= ct.length ? ct : tt;
      return short.every((tok) => long.some((l) => close(tok, l)));
    });
    return hit ? hit.count : 0;
  };

  // Load the user's state, and switch to the Punjab roster if applicable.
  useEffect(() => {
    localforage.getItem("location").then((l) => setPcmLocation(l || ""));
  }, []);

  useEffect(() => {
    if (pcmLocation === null) return; // wait until loaded
    if (pcmLocation === "Punjab") {
      setPcmRows(PUNJAB_PCM);
      setZonalRows(PUNJAB_ZONAL);
      setZonalsPresent(PUNJAB_ZONAL.length);
    }
  }, [pcmLocation]);

  // Load today's saved report if it exists
  useEffect(() => {
    if (pcmLocation === null) return; // wait until state is known
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.get(`${API_URL}/pcm-report/today${stateQuery}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data) {
          if (data.pcmRows && data.pcmRows.length) setPcmRows(data.pcmRows);
          if (data.zonalRows && data.zonalRows.length) setZonalRows(data.zonalRows);
          setNote(data.note || "");
          if (data.snapshot) {
            setMeetingsScheduled(data.snapshot.meetingsScheduled ?? "");
            setZonalsPresent(data.snapshot.zonalsPresent ?? DEFAULT_ZONAL.length);
            setZonalsAbsent(data.snapshot.zonalsAbsent ?? 0);
          }
        }
      } catch (e) {
        /* no saved report yet */
      }
    })();
  }, [pcmLocation]);

  // Load each PCM's meeting count for today (from the meeting dashboard data).
  useEffect(() => {
    if (pcmLocation === null) return;
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.get(`${API_URL}/pcm-report/mom-counts${stateQuery}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data && Array.isArray(data.counts)) setMomCounts(data.counts);
      } catch (e) {
        /* counts are best-effort */
      }
    })();
  }, [pcmLocation]);

  // Auto-computed snapshot values
  const snap = useMemo(() => {
    const present = pcmRows.filter((r) => r.attendance === "Present").length;
    const absent = pcmRows.filter((r) => r.attendance === "Absent").length;
    const vacant = pcmRows.filter((r) => r.attendance === "Vacant").length;
    const cabIssues = pcmRows.filter(
      (r) => r.attendance === "Present" && r.cabUsed === "No"
    ).length;
    const totalEsc = pcmRows.reduce(
      (sum, r) => sum + (parseInt(r.escalationRaised, 10) || 0),
      0
    );
    return {
      totalPcms: present + absent,
      pcmsPresent: present,
      pcmsAbsent: absent,
      vacantPcs: vacant,
      cabIssuesPcs: cabIssues,
      totalEscalations: totalEsc,
      zonalsPresent: parseInt(zonalsPresent, 10) || 0,
      zonalsAbsent: parseInt(zonalsAbsent, 10) || 0,
      meetingsScheduled: parseInt(meetingsScheduled, 10) || 0,
    };
  }, [pcmRows, zonalsPresent, zonalsAbsent, meetingsScheduled]);

  const updatePcm = (i, field, value) =>
    setPcmRows((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r))
    );
  const updateZonal = (i, field, value) =>
    setZonalRows((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r))
    );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `PCM_Activity_Report_${today.toISOString().split("T")[0]}`,
  });

  const buildPayload = async () => {
    const userName = (await localforage.getItem("userName")) || "";
    return {
      date: today.toISOString().split("T")[0],
      state: isPunjab ? "Punjab" : "",
      snapshot: snap,
      pcmRows,
      zonalRows,
      note,
      updatedByName: userName,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus("");
    try {
      const token = await localforage.getItem("token");
      await api.post(`${API_URL}/pcm-report/save`, await buildPayload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus("Saved ✓");
    } catch (e) {
      setStatus("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    setStatus("");
    try {
      const token = await localforage.getItem("token");
      await api.post(`${API_URL}/pcm-report/save`, await buildPayload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      await api.post(
        `${API_URL}/pcm-report/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus("Report emailed ✓");
    } catch (e) {
      setStatus("Could not email the report.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{`
        .pcm-table td, .pcm-table th { vertical-align: middle; text-align: center; }
        .pcm-table td textarea, .pcm-table td input, .pcm-table td select { margin: 0 auto; }
        @keyframes pcmFlash { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        .pcm-flash-banner {
          background: #b91c1c; color: #fff; text-align: center; font-weight: 800;
          padding: 12px 16px; border-radius: 8px; margin: 0 0 14px; font-size: 16px;
          letter-spacing: 0.3px; animation: pcmFlash 1s ease-in-out infinite;
        }
        @media print {
          .pcm-flash-banner {
            animation: none;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
      <Dashboard />
      <div className="pcm-page">
        <div className="pcm-toolbar">
          <h2>PCM Activity &amp; Attendance</h2>
          <div className="pcm-actions">
            <button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button className="pcm-secondary" onClick={handlePrint}>
              Download PDF
            </button>
            <button className="pcm-send" onClick={handleSendNow} disabled={sending}>
              {sending ? "Sending…" : "Email Report Now"}
            </button>
            {status && <span className="pcm-status">{status}</span>}
          </div>
        </div>
        <p className="pcm-hint">
          The report is emailed automatically every day at 8:00 PM IST to the
          leadership team. Use “Email Report Now” to send immediately.
        </p>

        <div className="pcm-doc" ref={printRef}>
          {SHOW_MOM_AS_NA && (
            <div className="pcm-flash-banner">{PCM_NOTICE}</div>
          )}
          {/* Snapshot */}
          <div className="pcm-section-title">
            Snapshot of PCM Activity &amp; Attendance
          </div>
          <table className="pcm-snapshot">
            <tbody>
              <tr>
                <th>Total No of PCMs</th>
                <td>{snap.totalPcms}</td>
                <th>PCMs Present ({snap.totalPcms})</th>
                <td>{snap.pcmsPresent}</td>
                <th>PCMs Absent</th>
                <td>{snap.pcmsAbsent}</td>
              </tr>
              <tr>
                <th>Vacant PCs</th>
                <td>{snap.vacantPcs}</td>
                <th>Zonals Present</th>
                <td>
                  <input
                    className="pcm-snap-input"
                    type="number"
                    value={zonalsPresent}
                    onChange={(e) => setZonalsPresent(e.target.value)}
                  />
                </td>
                <th>Zonals Absent</th>
                <td>
                  <input
                    className="pcm-snap-input"
                    type="number"
                    value={zonalsAbsent}
                    onChange={(e) => setZonalsAbsent(e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <th>Total Meetings Scheduled</th>
                <td>
                  <input
                    className="pcm-snap-input"
                    type="number"
                    value={meetingsScheduled}
                    onChange={(e) => setMeetingsScheduled(e.target.value)}
                  />
                </td>
                <th>Cab issues PCs</th>
                <td>{snap.cabIssuesPcs}</td>
                <th>Total Escalations</th>
                <td>{snap.totalEscalations}</td>
              </tr>
            </tbody>
          </table>

          {/* PCM Activity Report */}
          <div className="pcm-section-title">
            PCM Activity Report - {dateLabel}
          </div>
          <table className="pcm-table">
            <thead>
              <tr>
                <th>Sl. no.</th>
                <th>PCM Name</th>
                <th>{isPunjab ? "AC Mapped" : "PC Mapped"}</th>
                <th>MoM's</th>
                <th>Attendance</th>
                <th>Cab Used</th>
                <th>Escalation Raised</th>
                <th>Opposition Activities Raised</th>
                <th>Meeting Conducted</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {pcmRows.map((r, i) => (
                <tr key={i}>
                  <td className="pcm-num">{r.slNo || i + 1}</td>
                  <td>{r.pcmName}</td>
                  <td>{r.pcMapped}</td>
                  <td className="pcm-mom-count" style={{ textAlign: "center", fontWeight: 700 }}>
                    {SHOW_MOM_AS_NA ? "NA" : momCountFor(r.pcmName)}
                  </td>
                  <td>
                    <select
                      value={r.attendance}
                      onChange={(e) => updatePcm(i, "attendance", e.target.value)}
                    >
                      {ATTENDANCE.map((o) => (
                        <option key={o} value={o}>
                          {o || "Select"}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={r.cabUsed}
                      onChange={(e) => updatePcm(i, "cabUsed", e.target.value)}
                    >
                      {CAB.map((o) => (
                        <option key={o} value={o}>
                          {o || "Select"}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={r.escalationRaised}
                      onChange={(e) =>
                        updatePcm(i, "escalationRaised", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={r.oppositionRaised}
                      onChange={(e) =>
                        updatePcm(i, "oppositionRaised", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      rows={2}
                      value={r.meetingConducted}
                      onChange={(e) =>
                        updatePcm(i, "meetingConducted", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      rows={2}
                      value={r.note}
                      onChange={(e) => updatePcm(i, "note", e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Zonal Activity Tracker */}
          <div className="pcm-section-title">Zonal Activity Tracker</div>
          <table className="pcm-table">
            <thead>
              <tr>
                <th>Sl. no.</th>
                <th>{isPunjab ? "Region" : "Zone"}</th>
                <th>{isPunjab ? "AC Mapped" : "PCs Mapped"}</th>
                <th>Zonal</th>
                <th>Meetings Conducted</th>
              </tr>
            </thead>
            <tbody>
              {zonalRows.map((r, i) => (
                <tr key={i}>
                  <td className="pcm-num">{r.slNo || i + 1}</td>
                  <td>{r.zone}</td>
                  <td>{r.pcsMapped}</td>
                  <td>{r.zonal}</td>
                  <td>
                    <textarea
                      rows={2}
                      value={r.meetingsConducted}
                      onChange={(e) =>
                        updateZonal(i, "meetingsConducted", e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Note */}
          <div className="pcm-section-title">Note</div>
          <textarea
            className="pcm-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add any general notes for the day…"
          />
        </div>
      </div>
    </>
  );
};

export default PcmActivityTracker;
